import { Body, Button, Icon } from '@assembly-js/design-system';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { TextField } from 'src/components/TextField';
import RowDivider from 'src/legacy/components/RowDivider';
import { GoogleAuthButton } from 'src/legacy/components/UI/Buttons/GoogleAuthButton';
import { ensureError } from 'src/utils/Errors';

import { StudioPageFrame } from './StudioPageFrame';
import {
  STUDIO_BUTTON_FOCUS_CLASS,
  STUDIO_INPUT_FOCUS_CLASS,
  STUDIO_PRIMARY_BUTTON_CLASS,
} from './studioStyles';
import { STUDIO_ACCOUNT_ALREADY_EXISTS_CODE } from './utils/studioAuth';

interface StudioGetStartedFormValues {
  email: string;
}

export interface StudioGetStartedProps {
  onContinueWithGoogle: () => void;
  onContinueWithEmail: (email: string) => Promise<void>;
  // Optional copy overrides so the same input design can back both the signup
  // ("Get started") and login screens. Default to the signup copy.
  title?: string;
  subtitle?: string;
  submitLabel?: string;
}

export const StudioGetStarted = ({
  onContinueWithGoogle,
  onContinueWithEmail,
  title,
  subtitle,
  submitLabel,
}: StudioGetStartedProps) => {
  const { t } = useTranslation();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        email: Yup.string()
          .email(t(($) => $.page_onboarding.studio.email_invalid))
          .required(t(($) => $.page_onboarding.studio.email_required)),
      }),
    [t],
  );

  return (
    <StudioPageFrame>
      <Icon icon="AssemblyLogo" className="mb-3 h-8 w-8 text-black" />
      <Body className="font-studio text-2xl text-black text-center sm:text-[26px]">
        {title ?? t(($) => $.page_onboarding.studio.title)}
      </Body>
      <Body
        size="lg"
        className="mb-0 mt-3 font-studio text-center text-[#808080]"
      >
        {subtitle ?? t(($) => $.page_onboarding.studio.subtitle)}
      </Body>

      <Formik<StudioGetStartedFormValues>
        initialValues={{ email: '' }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setFieldError, setSubmitting }) => {
          try {
            await onContinueWithEmail(values.email.trim());
          } catch (error) {
            const normalizedError = ensureError(error) as Error & {
              code?: string;
            };
            const message =
              normalizedError.code === STUDIO_ACCOUNT_ALREADY_EXISTS_CODE
                ? t(($) => $.page_onboarding.studio.account_already_exists)
                : normalizedError.message;
            setFieldError('email', message);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
        }) => (
          <Form className="mt-12 flex w-full flex-col">
            <GoogleAuthButton
              onClick={onContinueWithGoogle}
              className={clsx(
                'hover:!bg-[#f5f5f0] [&_.MuiTypography-root]:!text-lg [&_.MuiTypography-root]:!font-normal',
                STUDIO_BUTTON_FOCUS_CLASS,
              )}
            />

            <RowDivider mt={0} mb={1.5}>
              <Body size="xs" className="text-[#9AA0A6]">
                {t(($) => $.page_onboarding.studio.or)}
              </Body>
            </RowDivider>

            <TextField
              sizeVariant="tall"
              fullWidth
              type="email"
              name="email"
              variant="outlined"
              className={STUDIO_INPUT_FOCUS_CLASS}
              label={t(($) => $.page_onboarding.studio.email_label)}
              placeholder={t(($) => $.page_onboarding.studio.email_placeholder)}
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              autoComplete="email"
              InputLabelProps={{ shrink: true }}
              error={Boolean(touched.email && errors.email)}
              helperText={(touched.email && errors.email) || ' '}
            />

            <Button
              type="submit"
              id="studioContinueWithEmail"
              className={clsx('mt-3 h-12 w-full', STUDIO_PRIMARY_BUTTON_CLASS)}
              variant="studio"
              label={
                submitLabel ??
                t(($) => $.page_onboarding.studio.continue_with_email)
              }
              disabled={isSubmitting}
              loading={isSubmitting}
            />
          </Form>
        )}
      </Formik>
    </StudioPageFrame>
  );
};
